import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

const KINDS = ['lookup', 'value', 'rule', 'question', 'check'];
const STATUSES = ['pending', 'done', 'skipped'];

/**
 * Parses a plan JSON string ({ goal, steps: [{ id, kind, title, detail, status }] })
 * into a normalized object, or null when the payload is not a plan. The server is the
 * authoritative normalizer (PlanSessionArtifactLane, C#); this only guards against a
 * malformed round-trip so the panel never renders broken rows.
 */
export const parsePlan = (json) => {
  try {
    const root = JSON.parse(safeString(json) || 'null');
    if (!root || typeof root !== 'object' || !Array.isArray(root.steps)) return null;
    const steps = root.steps
      .map((s, ix) => ({
        id: safeString(s?.id).trim() || `step${ix + 1}`,
        kind: KINDS.includes(safeString(s?.kind).trim().toLowerCase()) ? safeString(s.kind).trim().toLowerCase() : 'check',
        title: safeString(s?.title).trim(),
        detail: safeString(s?.detail).trim(),
        status: STATUSES.includes(safeString(s?.status).trim().toLowerCase()) ? safeString(s.status).trim().toLowerCase() : 'pending',
      }))
      .filter((s) => s.title);
    return { goal: safeString(root.goal).trim(), steps };
  } catch {
    return null;
  }
};

/** Serializes a plan back to the round-trip JSON contract. */
export const serializePlan = (plan) => JSON.stringify({
  goal: safeString(plan?.goal),
  steps: (Array.isArray(plan?.steps) ? plan.steps : []).map((s) => ({
    id: s.id, kind: s.kind, title: s.title, detail: s.detail, status: s.status,
  })),
});

const KIND_STYLES = {
  lookup: { background: '#e7f1ff', color: '#0a58ca', label: 'Lookup' },
  value: { background: '#fff3cd', color: '#664d03', label: 'Value' },
  rule: { background: '#e7e0f5', color: '#4b2a8a', label: 'Rule' },
  question: { background: '#f8d7da', color: '#842029', label: 'Question' },
  check: { background: '#e9ecef', color: '#495057', label: 'Check' },
};

const badgeStyle = (background, color) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  background,
  color,
  whiteSpace: 'nowrap',
});

const statusMark = (status) => {
  if (status === 'done') return { glyph: '\u2713', color: '#28a745', title: 'Done' };
  if (status === 'skipped') return { glyph: '\u2013', color: '#6c757d', title: 'Skipped' };
  return { glyph: '\u25CB', color: '#adb5bd', title: 'Pending' };
};

/**
 * The plan-first session's PLAN panel: the assistant's statement of what it will do
 * (one step per lookup, value, rule and check), rendered beside the chat with the
 * Approve button that opens the execute phase. Unapproved plans render expanded with
 * an amber "awaiting approval" header; once approved the panel collapses to a green
 * summary line (expandable) so the artifacts below get the room, while step statuses
 * keep updating as the assistant reports progress. A revised plan arrives unapproved
 * again (the dialog resets the flag), so the panel re-expands by itself.
 *
 * Props:
 * - plan: { goal, steps: [{ id, kind, title, detail, status }] } | null (no plan yet)
 * - approved: whether the user approved THIS plan revision
 * - onApprove(): the user approves the plan
 * - disabled?: while a turn is running
 * - blocked?: the assistant's latest turn is a question the user has not answered —
 *   the header reads "Awaiting your answer" and Approve is held (approving would only
 *   re-send a go-ahead the assistant cannot act on)
 */
export const PlanPanel = ({ plan, approved, onApprove, disabled, blocked }) => {
  const [expanded, setExpanded] = React.useState(!approved);
  // Track the approval flag: a fresh (unapproved) revision always re-expands.
  React.useEffect(() => { setExpanded(!approved); }, [approved, plan]);

  if (!plan) {
    return (
      <div
        data-tagname="planPanel"
        style={{ border: '1px dashed #ced4da', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#6c757d', lineHeight: 1.5 }}
      >
        <span style={{ fontWeight: 700, marginRight: 6 }}>{t('Plan')}</span>
        {t('The assistant proposes a plan before changing anything. Describe what you want; the plan appears here for your approval.')}
      </div>
    );
  }

  const steps = Array.isArray(plan.steps) ? plan.steps : [];
  const done = steps.filter((s) => s.status === 'done').length;
  const headerTone = approved
    ? { background: '#d1e7dd', color: '#0f5132', border: '#a3cfbb' }
    : { background: '#fff3cd', color: '#664d03', border: '#ffe69c' };

  return (
    <div data-tagname="planPanel" style={{ border: `1px solid ${headerTone.border}`, borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); } }}
        style={{
          background: headerTone.background,
          color: headerTone.color,
          padding: '6px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 11, width: 12, flexShrink: 0 }}>{expanded ? '\u25BE' : '\u25B8'}</span>
        <span style={{ fontWeight: 700, fontSize: 12.5 }}>{t('Plan')}</span>
        <span style={{ fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={plan.goal}>
          {plan.goal}
        </span>
        <span style={badgeStyle('rgba(255,255,255,0.7)', headerTone.color)}>
          {approved ? `${done}/${steps.length} ${t('done')}` : (blocked ? t('Awaiting your answer') : t('Awaiting approval'))}
        </span>
        {!approved && (
          // The wrapper stops the click from also toggling the header.
          <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <AccentButton
              tagName="approvePlan"
              size="sm"
              disabled={!!disabled || !!blocked}
              onClick={(e) => { onApprove?.(); e?.promise?.(); }}
              tooltip={blocked
                ? t('Answer the assistant\'s question in the chat first — the plan cannot proceed until you do.')
                : t('Approve the plan: the assistant will carry it out and produce the artifacts. You can still ask for changes afterwards.')}
            >
              {t('Approve')}
            </AccentButton>
          </span>
        )}
      </div>
      {expanded && (
        <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, background: '#fff', maxHeight: 260, overflowY: 'auto' }}>
          {steps.map((step, ix) => {
            const kind = KIND_STYLES[step.kind] ?? KIND_STYLES.check;
            const mark = statusMark(step.status);
            return (
              <div key={step.id || ix} data-tagname={`planStep_${step.id}`} style={{ display: 'grid', gridTemplateColumns: '18px auto 1fr', gap: 8, alignItems: 'start' }}>
                <span title={t(mark.title)} style={{ color: mark.color, fontWeight: 700, fontSize: 13, lineHeight: '18px' }}>{mark.glyph}</span>
                <span style={{ ...badgeStyle(kind.background, kind.color), marginTop: 1 }}>{t(kind.label)}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: step.status === 'skipped' ? '#6c757d' : '#212529', textDecoration: step.status === 'skipped' ? 'line-through' : 'none' }}>
                    {step.title}
                  </div>
                  {step.detail && (
                    <div style={{ fontSize: 12, color: '#495057', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{step.detail}</div>
                  )}
                </div>
              </div>
            );
          })}
          {steps.length === 0 && (
            <div style={{ fontSize: 12, color: '#6c757d', fontStyle: 'italic' }}>{t('The plan has no steps.')}</div>
          )}
        </div>
      )}
    </div>
  );
};
